import { Box, Button } from '@mui/material';
import { i18n } from '../../translate/i18n';
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
    ReactFlow,
    applyNodeChanges,
    applyEdgeChanges,
    addEdge,
    Background,
    BackgroundVariant,
    Position,
    useReactFlow,
    Node,
    OnNodesChange,
    OnEdgesChange,
    Connection,
    EdgeTypes,
} from 'reactflow';
import '@xyflow/react/dist/style.css';
import React from 'react';

import { StartNode } from './Nodes/StartNode';
import { ConditionNode } from './Nodes/ConditionNode';
import { EndNode } from './Nodes/EndNode';
import EdgeLiteBot from './edge';
import NodeModal from './Nodes/NodeModal';
import notify from '../../helpers/toast';
import api from '../../services/api';
import { useConexaBotVariables } from '../../hooks/useConexaBotVariables';
import { useHistory } from "react-router-dom";

export interface INode extends Node {
    id: string;
    position: { x: number; y: number };
    type: 'start' | 'condition' | 'end';
    data: {
        id: string;
        label: string;
        type: 'start' | 'condition' | 'end';
        subtype:
        | 'start'
        | 'transfer'
        | 'finish'
        | 'question'
        | 'message'
        | 'simpleCondition'
        | 'compoundCondition'
        | 'await'
        | 'variable'
        | 'quickReply'
        | 'openingHour';
        message?: string;
        question?: string;
        variable?: string;
        departmentId?: number;
        await?: string;
        simpleCondition?: { 
            variable: string; 
            operator: "equalTo" | "notEqual" | "contain" |  "doesNotContain" | "greaterThan" | "greatherOrEqualTo" | "lessThan" | "lessOrEqualTo" | "isEmpty" | "isCPF" | "isCEP" | "isCNPJ" | "isPhone" | "",
            value: string 
        };
        compoundCondition?: { variable: string; options: { id: string, name: string; value: string[] }[] };
        quickReplyId?: number;
    };
    sourcePosition?: Position;
    targetPosition?: Position;
}

type Edge<T = any> = any;

interface IEdge extends Edge {
    id: string;
    source: string;
    target: string;
}

interface IFlowCanvasProps {
    conexaBotId: number;
    name: string;
    initialNodes: INode[];
    initialEdges: IEdge[];
    departments: any[] | null;
    quickReplies: any[] | null;
    conclusionMessages: any[] | null;
    openingHours: any[] | null;
}

const FlowCanvas = React.memo(({ conexaBotId, initialNodes, initialEdges, departments, quickReplies, conclusionMessages, openingHours }: IFlowCanvasProps) => {
    // id incremental
    let id = 1;

    if (initialNodes && initialNodes.length > 0) {
        // extrai o maior número dos IDs existentes
        const maxId = Math.max(
            ...initialNodes.map(node => parseInt(String(node.id).replace("#", ""), 10) || 0)
        );
        id = maxId + 1;
    }

    const getId = () => `#${id++}`;

    const [nodes, setNodes] = useState<INode[] | any[]>([]);
    const [edges, setEdges] = useState<IEdge[]>([]);
    const [selectedCustomNode, setSelectedCustomNode] = useState<INode['data'] | null>(null);
    const { screenToFlowPosition } = useReactFlow();
    const history = useHistory();
    const { variables, reset } = useConexaBotVariables();

    // memorizar tipos de nós
    const nodeTypes = useMemo(
        () => ({
            start: StartNode,
            condition: (props: any) => (
                <ConditionNode
                    {...props}
                    onEdit={(event: React.MouseEvent, node: Node['data']) =>
                        setSelectedCustomNode(node as INode['data'])
                    }
                    quickReplies={quickReplies}
                    openingHours={openingHours}
                />
            ),
            end: (props: any) => (
                <EndNode
                    {...props}
                    onEdit={(event: React.MouseEvent, node: Node['data']) =>
                        setSelectedCustomNode(node as INode['data'])
                    }
                    departments={departments}
                    conclusionMessages={conclusionMessages}
                />
            ),
        }),
        []
    );

    // memorizar tipos de arestas
    const edgeTypes: EdgeTypes = useMemo(
        () => ({
            default: EdgeLiteBot,
        }),
        []
    );

    // Handlers de nodes e edges
    const onNodesChange: OnNodesChange = useCallback(
        (changes: any) => setNodes((nds) => applyNodeChanges(changes, nds)),
        []
    );

    const onEdgesChange: OnEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    );

    const onConnect = useCallback(
        (params: Connection) => setEdges((eds) => addEdge({ ...params, type: 'default' }, eds)),
        []
    );

    const onClose = useCallback(() => {
        setSelectedCustomNode(null);
    }, []);

    // Inicializar nodes/edges apenas quando props mudarem
    useEffect(() => {
        if (initialNodes?.length) setNodes(initialNodes);
        if (initialEdges?.length) setEdges(initialEdges);
    }, [initialNodes, initialEdges]);

    // Drag events
    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            const rawData = event.dataTransfer.getData('application/reactflow');
            if (!rawData) return;

            const nodeData = JSON.parse(rawData) as {
                label: INode['data']['label'];
                type: INode['type'];
                subtype: INode['data']['subtype'];
            };

            if (!nodeData?.type || !nodeData?.subtype) {
                notify('error', i18n.t('ConexaBot.messages.INVALID_TYPE'));
                return;
            }

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            let tmpId = getId();
            const newNode: INode = {
                id: tmpId,
                type: nodeData.type,
                position,
                data: {
                    id: tmpId,
                    label: nodeData.label,
                    type: nodeData.type,
                    subtype: nodeData.subtype,
                },
            };

            setNodes((nds) => {
                // verificar se o componente de inicio já existe
                if (nodeData.type == 'start') {
                    const idx = nds.findIndex((n) => n.data.type == 'start');
                    if (idx > -1) {
                        notify('error', i18n.t('ConexaBot.messages.START_ALREADY_EXISTS'));
                        return nds; // não altera
                    }
                }
                return [...nds, newNode];
            });
        },
        [screenToFlowPosition]
    );

    const handleClose = () => {
        reset();
        history.push('/ConexaBot');
    }

    const customError = (msg: string) => {
        const err = new Error();
        (err as any).response = {
            data: {
                message: msg
            }
        };
        throw err;
    }

    const handleSave = async () => {
        try {

            // verificar se existem componentes sem ligação
            let hasStart = false;
            let hasEnd = false;
            for (const nd of nodes) {

                // resgatar ligações dos edges
                const ndEdges = edges.filter(e => e.source == nd.id || e.target == nd.id);

                if (nd.type == "start") {
                    const idx = ndEdges.findIndex(e => e.source == nd.id);
                    if (idx == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES").replaceAll("{{name}}", nd.data.label));
                    hasStart = true;
                } else if (nd.type == "end") {
                    const idx = ndEdges.findIndex(e => e.target == nd.id);
                    if (idx == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES").replaceAll("{{name}}", nd.data.label));
                    hasEnd = true;
                } else {
                    if (nd.data.subtype == "compoundCondition") {

                        const tgtIndex = ndEdges.findIndex(e => e.target == nd.id);
                        if (tgtIndex == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES_TARGET").replaceAll("{{name}}", nd.data.label));

                        const falseIndex = ndEdges.findIndex(e => e.sourceHandle == `${nd.id}-false`);
                        if (falseIndex == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES_SOURCE_OPTION").replaceAll("{{name}}", nd.data.label).replaceAll("{{option}}", i18n.t("ConexaBot.nodes.condition.false")));

                        // verificar cada opção
                        if (nd.data?.compoundCondition?.options) {
                            for (const op of nd.data.compoundCondition.options) {
                                const idx = ndEdges.findIndex(e => e.sourceHandle == `${nd.id}-${op.name}`);
                                if (idx == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES_SOURCE_OPTION").replaceAll("{{name}}", nd.data.label).replaceAll("{{option}}", op.name));
                            }
                        }
                        
                    } else if (nd.data.subtype == "simpleCondition") {
                        const tgtIndex = ndEdges.findIndex(e => e.target == nd.id);
                        if (tgtIndex == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES_TARGET").replaceAll("{{name}}", nd.data.label));

                        const falseIndex = ndEdges.findIndex(e => e.sourceHandle == `${nd.id}-false`);
                        if (falseIndex == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES_SOURCE_OPTION").replaceAll("{{name}}", nd.data.label).replaceAll("{{option}}", i18n.t("ConexaBot.nodes.condition.false")));
                        
                        const trueIndex = ndEdges.findIndex(e => e.sourceHandle == `${nd.id}-true`);
                        if (trueIndex == -1) customError(i18n.t("ConexaBost.messages.INVALID_EDGES_SOURCE_OPTION").replaceAll("{{name}}", nd.data.label).replaceAll("{{option}}", i18n.t("ConexaBot.nodes.condition.true")));
                    } else {
                        const srcIndex = ndEdges.findIndex(e => e.source == nd.id);
                        if (srcIndex == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES_SOURCE").replaceAll("{{name}}", nd.data.label));
                        const tgtIndex = ndEdges.findIndex(e => e.target == nd.id);
                        if (tgtIndex == -1) customError(i18n.t("conexaBots.messages.INVALID_EDGES_TARGET").replaceAll("{{name}}", nd.data.label));
                    }
                }
            }

            if (hasStart == false) customError(i18n.t("conexaBots.messages.INVALID_EDGES_START"));
            if (hasEnd == false) customError(i18n.t("conexaBots.messages.INVALID_EDGES_END"));

            // cadastrar
            const { data } = await api.post('/ConexaBot/updateFlow', {
                id: conexaBotId,
                nodes: JSON.stringify(nodes), 
                edges: JSON.stringify(edges),
                variables: Array.isArray(variables) && variables.length > 0 ? JSON.stringify(variables) : null
            });

            notify("success", data?.message ? i18n.t(data.message) : i18n.t("generalMessages.SUCCESSFULLY_SAVED"));
        } catch (err: any) {
            console.error({err});
            notify('error', err?.response?.data?.message ? i18n.t(err.response.data.message) : i18n.t('generalMessages.INTERNAL_ERROR'));
        }
    };

    return (
        <>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                edgeTypes={edgeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                onDragOver={onDragOver}
                onDrop={onDrop}
                fitView
            >
                <Background color="#1565c0" variant={BackgroundVariant.Dots} />
                <NodeModal
                    open={selectedCustomNode !== null}
                    onClose={onClose}
                    data={selectedCustomNode ?? undefined}
                    departments={departments}
                    quickReplies={quickReplies}
                    conclusionMessages={conclusionMessages}
                    openingHours={openingHours}
                    nodes={Array.isArray(nodes) ? nodes.filter(n => n.data.type != 'start' && n.data.id != selectedCustomNode?.id) : []}
                    edges={Array.isArray(edges) ? edges : []}
                />
            </ReactFlow>

            {/* Botões */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10px',
                    width: '100%',
                    textAlign: 'center',
                }}
            >
                <Button variant="contained" size="small" color="error" onClick={handleClose}>
                    {i18n.t('buttons.close')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    size="small"
                    color="success"
                    sx={{ marginLeft: '10px' }}
                >
                    {i18n.t('buttons.save')}
                </Button>
            </Box>
        </>
    );
});

export default FlowCanvas;
